import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ClassroomDto, DayReviewDto } from '../../api/types.js';
import { useApi } from '../../api/ApiProvider.js';
import { isoDate } from '../../lib/localDate.js';
import { searchUrlFor } from '../../lib/searchUrls.js';
import { DayExportView } from '../Calendar/DayExportView.js';
import styles from './ClassroomHistory.module.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface YM {
  y: number;
  m: number; // 0-based month
}

function parseIso(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { y: y ?? 1970, m: (m ?? 1) - 1, d: d ?? 1 };
}

function ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** "Mon, Jun 2, 2026" — local, no timezone shift (built from the date parts). */
function formatLong(iso: string): string {
  const { y, m, d } = parseIso(iso);
  return new Date(y, m, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Jun 2" — compact label for the recent-days list. */
function formatShort(iso: string): string {
  const { y, m, d } = parseIso(iso);
  return new Date(y, m, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function shiftMonth({ y, m }: YM, delta: number): YM {
  const next = new Date(y, m + delta, 1);
  return { y: next.getFullYear(), m: next.getMonth() };
}

/**
 * Top-level, teacher-facing "Day in review" history for a classroom. Lives as
 * its own tab in ClassroomDetail (a peer of Settings) — never on the live
 * morning-meeting screens, which are student-facing.
 *
 * Two ways in (both, per the picker decision): a month calendar that marks the
 * days with recorded activity, and a recent-days shortcut list. Selecting a day
 * loads its read-only review (message, question, weather, to-do, turn-in, and
 * the searches that were run, tap-to-reopen) plus a print / Save-as-PDF export.
 */
export function ClassroomHistory({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const classroomId = classroom.id;
  const today = isoDate();

  const daysQuery = useQuery({
    queryKey: ['classroom-days', classroomId],
    queryFn: () => api.dayHistory.listDays(classroomId, 366),
  });
  const activity = useMemo(() => new Set(daysQuery.data ?? []), [daysQuery.data]);
  const recent = daysQuery.data ?? [];

  // Selected day + the month the grid is showing. Both initialise to the most
  // recent day that has history (falling back to today) once the list loads.
  const [selected, setSelected] = useState<string | null>(null);
  const [view, setView] = useState<YM>(() => {
    const { y, m } = parseIso(today);
    return { y, m };
  });

  useEffect(() => {
    if (selected == null && recent.length > 0) {
      const latest = recent[0]!;
      setSelected(latest);
      const { y, m } = parseIso(latest);
      setView({ y, m });
    }
  }, [recent, selected]);

  const effectiveSelected = selected ?? recent[0] ?? today;

  const reviewQuery = useQuery({
    queryKey: ['day-review', classroomId, effectiveSelected],
    queryFn: () => api.dayHistory.getReview(classroomId, effectiveSelected),
    enabled: Boolean(effectiveSelected),
  });
  const review = reviewQuery.data ?? null;

  const pick = (iso: string) => {
    setSelected(iso);
    const { y, m } = parseIso(iso);
    setView({ y, m });
  };

  // Build the month grid (leading blanks + each day).
  const firstWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (string | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => ymd(view.y, view.m, i + 1)),
  ];

  return (
    <div className={`glass glass--solid ${styles.panel}`} data-testid="classroom-history">
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.heading}>History</h2>
          <p className={styles.description}>
            Every school day is kept. Pick a day to see what was on the board and what the class
            looked up, or export it.
          </p>
        </div>
      </header>

      <div className={styles.layout}>
        <div className={styles.picker}>
          <div className={styles.calendar} data-testid="history-calendar">
            <div className={styles.calHead}>
              <button
                type="button"
                className={styles.calNav}
                onClick={() => setView((v) => shiftMonth(v, -1))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <span className={styles.calTitle}>
                {MONTHS[view.m]} {view.y}
              </span>
              <button
                type="button"
                className={styles.calNav}
                onClick={() => setView((v) => shiftMonth(v, 1))}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            <div className={styles.calGrid}>
              {WEEKDAYS.map((w) => (
                <span key={w} className={styles.calWeekday}>
                  {w}
                </span>
              ))}
              {cells.map((iso, i) => {
                if (!iso) return <span key={`blank-${i}`} className={styles.calBlank} />;
                const { d } = parseIso(iso);
                const isFuture = iso > today;
                const hasActivity = activity.has(iso);
                const isSelected = iso === effectiveSelected;
                const isToday = iso === today;
                const classes = [styles.calDay];
                if (isSelected) classes.push(styles.calDaySelected);
                if (isToday) classes.push(styles.calDayToday);
                return (
                  <button
                    key={iso}
                    type="button"
                    className={classes.join(' ')}
                    disabled={isFuture}
                    aria-pressed={isSelected}
                    onClick={() => pick(iso)}
                    data-testid={`history-day-${iso}`}
                    data-has-activity={hasActivity ? 'true' : undefined}
                  >
                    {d}
                    {hasActivity ? <span className={styles.dot} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.recent}>
            <h3 className={styles.recentTitle}>Recent days</h3>
            {daysQuery.isLoading ? (
              <p className={styles.empty}>Loading…</p>
            ) : recent.length === 0 ? (
              <p className={styles.empty}>No history yet. It builds as you teach.</p>
            ) : (
              <ul className={styles.recentList} data-testid="history-recent">
                {recent.map((iso) => (
                  <li key={iso}>
                    <button
                      type="button"
                      className={
                        iso === effectiveSelected
                          ? `${styles.recentItem} ${styles.recentItemActive}`
                          : styles.recentItem
                      }
                      onClick={() => pick(iso)}
                      data-testid={`history-recent-${iso}`}
                    >
                      {formatShort(iso)}
                      {iso === today ? <span className={styles.todayTag}>Today</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className={styles.review} data-testid="history-review">
          <div className={styles.reviewHead}>
            <h3 className={styles.reviewDate}>{formatLong(effectiveSelected)}</h3>
            <button
              type="button"
              className={`glass-pill ${styles.exportBtn}`}
              onClick={() => window.print()}
              disabled={!review}
              data-testid="history-export"
            >
              Export / PDF
            </button>
          </div>

          {reviewQuery.isLoading ? (
            <p className={styles.empty}>Loading…</p>
          ) : review ? (
            <DayReviewBody review={review} />
          ) : (
            <p className={styles.empty}>Nothing recorded for this day.</p>
          )}
        </div>
      </div>

      {/* Print-only; an @media print block reveals just this for Save-as-PDF. */}
      <DayExportView
        classroomName={classroom.name}
        dateLabel={formatLong(effectiveSelected)}
        review={review}
      />
    </div>
  );
}

const has = (s: string) => s.trim().length > 0;

function DayReviewBody({ review }: { review: DayReviewDto }) {
  const isEmpty =
    !has(review.message) &&
    !has(review.questionDaily) &&
    !review.weather &&
    review.todo.length === 0 &&
    review.turnIn.length === 0 &&
    review.searches.length === 0;

  if (isEmpty) {
    return <p className={styles.empty}>Nothing was set or looked up on this day.</p>;
  }

  return (
    <div className={styles.sections}>
      {has(review.message) ? (
        <section className={styles.section} data-testid="review-message">
          <h4 className={styles.sectionTitle}>Message</h4>
          <p className={styles.sectionBody}>{review.message}</p>
        </section>
      ) : null}

      {has(review.questionDaily) ? (
        <section className={styles.section} data-testid="review-question">
          <h4 className={styles.sectionTitle}>Question of the day</h4>
          <p className={styles.sectionBody}>{review.questionDaily}</p>
          {has(review.questionDailyAnswer) ? (
            <p className={styles.sectionAnswer}>Answer: {review.questionDailyAnswer}</p>
          ) : null}
        </section>
      ) : null}

      {review.weather ? (
        <section className={styles.section} data-testid="review-weather">
          <h4 className={styles.sectionTitle}>Weather</h4>
          <p className={styles.sectionBody}>
            {[
              review.weather.kind,
              review.weather.temperature != null ? `${review.weather.temperature}°` : null,
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
        </section>
      ) : null}

      {review.todo.length > 0 ? (
        <section className={styles.section} data-testid="review-todo">
          <h4 className={styles.sectionTitle}>To-Do</h4>
          <ul className={styles.list}>
            {review.todo.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {review.turnIn.length > 0 ? (
        <section className={styles.section} data-testid="review-turnin">
          <h4 className={styles.sectionTitle}>Turn-In</h4>
          <ul className={styles.list}>
            {review.turnIn.map((t) => (
              <li key={t.subjectId}>
                <strong>{t.subjectName}:</strong> {t.assignment}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {review.searches.length > 0 ? (
        <section className={styles.section} data-testid="review-searches">
          <h4 className={styles.sectionTitle}>Searches</h4>
          <ul className={styles.searchList}>
            {review.searches.map((s) => (
              <li key={s.id}>
                <a
                  href={searchUrlFor(s.kind, s.engine, s.query)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.searchLink}
                >
                  <span className={styles.searchKind}>{s.kind === 'youtube' ? '▶' : '🔎'}</span>
                  {s.query}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
